import React, { useCallback, useEffect, useState } from "react";
import { Card } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { Textarea } from "@/components/ui/textarea";
import { BookOpenText, Sparkles, Loader2, Download, ShieldCheck, Undo2, Save } from "lucide-react";
import { toast } from "sonner";
import { apiClient } from "@/lib/apiClient";
import { ManagementReportComparison } from "./ManagementReportComparison";

const chf = (v) => (v == null ? "–" : `CHF ${Number(v).toLocaleString("de-CH", { maximumFractionDigits: 0 })}`);
const pct = (v) => (v == null ? "–" : `${Number(v).toFixed(1)} %`);

/** Lagebericht OR 961c: KI-Entwurf aus Bilanz/ER/Geldfluss → bearbeiten → freigeben → PDF (im Jahresabschluss-Archiv). */
export function ManagementReportCard({ companyId }) {
  const [year, setYear] = useState(new Date().getFullYear() - (new Date().getMonth() < 3 ? 1 : 0));
  const [data, setData] = useState(null);
  const [sections, setSections] = useState({});
  const [hints, setHints] = useState("");
  const [busy, setBusy] = useState("");
  const load = useCallback(() => {
    if (!companyId) return;
    apiClient.get(`/companies/${companyId}/management-report`, { params: { year } }).then((r) => { setData(r.data); setSections(r.data.sections || {}); setHints(r.data.hints || ""); }).catch(() => setData(null));
  }, [companyId, year]);
  useEffect(load, [load]);

  const run = async (label, fn, ok) => {
    setBusy(label);
    try { const r = await fn(); setData(r.data); setSections(r.data.sections || {}); if (ok) toast.success(ok); }
    catch (e) { toast.error(e.response?.data?.detail || "Aktion fehlgeschlagen"); } finally { setBusy(""); }
  };
  const generate = () => run("generate", () => apiClient.post(`/companies/${companyId}/management-report/generate`, { hints }, { params: { year } }), "KI-Entwurf erstellt – bitte prüfen und bei Bedarf anpassen");
  const save = () => run("save", () => apiClient.put(`/companies/${companyId}/management-report`, { sections, hints }, { params: { year } }), "Entwurf gespeichert");
  const approve = (v) => run("approve", () => apiClient.post(`/companies/${companyId}/management-report/approve`, null, { params: { year, approve: v } }), v ? "Lagebericht freigegeben – wird dem Jahresabschluss-Archiv beigelegt" : "Freigabe aufgehoben");
  const pdf = async () => {
    try {
      const r = await apiClient.get(`/companies/${companyId}/management-report/pdf`, { params: { year }, responseType: "blob" });
      const url = URL.createObjectURL(r.data);
      Object.assign(document.createElement("a"), { href: url, download: `Lagebericht-${year}.pdf` }).click();
      URL.revokeObjectURL(url);
    } catch { toast.error("PDF konnte nicht erstellt werden"); }
  };
  if (!data) return null;
  const approved = data.status === "approved";
  const k = data.kpis || {};
  return (
    <Card className="p-4 border border-slate-200 bg-white mt-6" data-testid="management-report-card">
      <div className="flex flex-wrap items-start justify-between gap-2 mb-3">
        <div className="flex items-start gap-2">
          <BookOpenText className="h-4 w-4 text-violet-700 mt-0.5" />
          <div>
            <div className="text-sm font-medium text-slate-900 inline-flex items-center gap-2">Lagebericht {year} (OR 961c)
              <span className={`rounded px-2 py-0.5 text-[11px] ${approved ? "bg-emerald-100 text-emerald-800" : data.status === "draft" ? "bg-amber-100 text-amber-800" : "bg-slate-100 text-slate-600"}`} data-testid="management-report-status">{approved ? `Freigegeben · ${data.approved_by}` : data.status === "draft" ? "Entwurf" : "Noch nicht erstellt"}</span>
            </div>
            <div className="text-[11px] text-slate-500">KI formuliert Geschäftsverlauf, Lage, Kennzahlen, Auftragslage, Mitarbeitende, Risikobeurteilung, Aussergewöhnliches und Ausblick aus Bilanz, Erfolgsrechnung und Geldflussrechnung. Erst nach Freigabe landet der Bericht im Jahresabschluss-Archiv.</div>
          </div>
        </div>
        <div className="flex items-center gap-2">
          <select value={year} onChange={(e) => setYear(Number(e.target.value))} className="h-8 rounded border border-slate-200 px-2 text-sm" data-testid="management-report-year">
            {[0, -1, -2, -3].map((d) => new Date().getFullYear() + d).map((y) => <option key={y} value={y}>{y}</option>)}
          </select>
          {data.status !== "none" && <Button size="sm" variant="outline" onClick={pdf} data-testid="management-report-pdf"><Download className="h-4 w-4 mr-1" />PDF</Button>}
        </div>
      </div>
      {data.kpis && (
        <div className="flex flex-wrap gap-1.5 mb-3 text-[11px]" data-testid="management-report-kpis">
          {[["Umsatz", chf(k.umsatz)], ["Ergebnis", chf(k.jahresergebnis)], ["EBIT-Marge", pct(k.ebit_marge_pct)], ["EK-Quote", pct(k.eigenkapitalquote_pct)], ["Liquiditätsgrad 2", pct(k.liquiditaetsgrad_2_pct)], ["Geldfluss Betrieb", chf(k.geldfluss_betrieb)], ["Mitarbeitende", k.mitarbeitende ?? "–"]].map(([l, v]) => (
            <span key={l} className="rounded-full bg-slate-100 px-2 py-0.5 text-slate-700">{l}: <b>{v}</b></span>
          ))}
        </div>
      )}
      {data.kpis && <ManagementReportComparison kpis={k} year={year} />}
      {!approved && (
        <div className="mb-3">
          <label className="text-xs text-slate-600">Stichworte der Geschäftsleitung (optional – fliessen in Geschäftsverlauf und Ausblick ein)</label>
          <Textarea value={hints} onChange={(e) => setHints(e.target.value)} rows={2} placeholder="z. B. neuer Standort eröffnet, Grosskunde gewonnen, Investition in Maschinen geplant …" className="mt-1 text-sm" data-testid="management-report-hints" />
          <Button size="sm" className="mt-2 bg-violet-600 hover:bg-violet-700" disabled={!!busy} onClick={generate} data-testid="management-report-generate">
            {busy === "generate" ? <Loader2 className="h-4 w-4 animate-spin mr-1" /> : <Sparkles className="h-4 w-4 mr-1" />}{data.status === "none" ? "KI-Entwurf erzeugen" : "KI-Entwurf neu erzeugen"}
          </Button>
        </div>
      )}
      {data.status !== "none" && (
        <div className="space-y-3">
          {Object.entries(data.section_labels).map(([key, label]) => (
            <div key={key} data-testid={`management-report-section-${key}`}>
              <div className="text-xs font-semibold text-slate-700 mb-1">{label}</div>
              {approved ? <p className="text-sm text-slate-800 whitespace-pre-wrap leading-relaxed">{sections[key] || "–"}</p>
                : <Textarea value={sections[key] || ""} onChange={(e) => setSections((s) => ({ ...s, [key]: e.target.value }))} rows={3} className="text-sm" data-testid={`management-report-text-${key}`} />}
            </div>
          ))}
          <div className="flex flex-wrap gap-2 pt-1">
            {approved ? (
              <Button size="sm" variant="outline" disabled={!!busy} onClick={() => approve(false)} data-testid="management-report-unapprove"><Undo2 className="h-4 w-4 mr-1" />Freigabe aufheben</Button>
            ) : (
              <>
                <Button size="sm" variant="outline" disabled={!!busy} onClick={save} data-testid="management-report-save">{busy === "save" ? <Loader2 className="h-4 w-4 animate-spin mr-1" /> : <Save className="h-4 w-4 mr-1" />}Entwurf speichern</Button>
                <Button size="sm" className="bg-emerald-600 hover:bg-emerald-700" disabled={!!busy} onClick={async () => { await save(); approve(true); }} data-testid="management-report-approve"><ShieldCheck className="h-4 w-4 mr-1" />Freigeben</Button>
              </>
            )}
          </div>
        </div>
      )}
    </Card>
  );
}
